import "../../scss/parts/altcoin-season.scss";

(() => {
  const widgetRoots = document.querySelectorAll(
    ".cryptonisation-altcoin-season",
  );

  if (widgetRoots.length === 0) {
    return;
  }

  const STATUS_CLASS_MAP = {
    bitcoin: "bitcoin",
    neutral: "neutral",
    altcoin: "altcoin",
  };

  const TIMEFRAMES = [7, 30, 90, 365];
  let activeTooltip = null;

  const escapeHtml = (value) =>
    String(value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");

  const readJsonScript = (widgetRoot, className) => {
    const node = widgetRoot.querySelector(className);

    if (!node || !node.textContent) {
      throw new Error(`Missing JSON script: ${className}`);
    }

    return JSON.parse(node.textContent);
  };

  const formatDate = (lang, isoDate) => {
    const formatter = new Intl.DateTimeFormat(lang, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    return formatter.format(new Date(isoDate));
  };

  const formatDateTime = (lang, isoDate) => {
    const formatter = new Intl.DateTimeFormat(lang, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

    return formatter.format(new Date(isoDate));
  };

  const formatBrowserTimezoneOffset = (date) => {
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absoluteMinutes = Math.abs(offsetMinutes);
    const hours = Math.floor(absoluteMinutes / 60);
    const minutes = absoluteMinutes % 60;

    if (minutes === 0) {
      return `UTC${sign}${hours}`;
    }

    return `UTC${sign}${hours}:${String(minutes).padStart(2, "0")}`;
  };

  const formatNumber = (lang, value, maximumFractionDigits = 0) => {
    const formatter = new Intl.NumberFormat(lang, {
      maximumFractionDigits,
      minimumFractionDigits: 0,
    });

    return formatter.format(value);
  };

  const resolveDurationUnit = (lang, days, messages) => {
    const pluralRules = new Intl.PluralRules(lang);
    const pluralCategory = pluralRules.select(days);

    if (
      pluralCategory === "one" &&
      typeof messages.duration_day_one === "string"
    ) {
      return messages.duration_day_one;
    }

    if (
      pluralCategory === "few" &&
      typeof messages.duration_day_few === "string"
    ) {
      return messages.duration_day_few;
    }

    if (
      pluralCategory === "many" &&
      typeof messages.duration_day_many === "string"
    ) {
      return messages.duration_day_many;
    }

    if (typeof messages.duration_day_other === "string") {
      return messages.duration_day_other;
    }

    throw new Error("Duration unit localization is not defined.");
  };

  const formatDurationDays = (lang, days, messages) => {
    if (typeof messages.duration_pattern !== "string") {
      throw new Error("Duration pattern localization is not defined.");
    }

    const formattedValue = formatNumber(lang, days, 0);
    const durationUnit = resolveDurationUnit(lang, days, messages);

    return messages.duration_pattern.replace(
      "%1$s",
      `${formattedValue} ${durationUnit}`,
    );
  };

  const openTooltip = (tooltip) => {
    if (activeTooltip && activeTooltip !== tooltip) {
      closeTooltip(activeTooltip);
    }

    tooltip.dataset.tooltipOpen = "true";
    tooltip.setAttribute("aria-expanded", "true");
    activeTooltip = tooltip;

    window.requestAnimationFrame(() => {
      if (tooltip.dataset.tooltipOpen === "true") {
        positionTooltip(tooltip);
      }
    });
  };

  const closeTooltip = (tooltip) => {
    if (!tooltip) {
      return;
    }

    const tooltipContent = tooltip.querySelector(
      ".cryptonisation-altcoin-season__info-tooltip-content",
    );

    if (!(tooltipContent instanceof HTMLElement)) {
      throw new Error("Tooltip content is missing.");
    }

    tooltip.dataset.tooltipOpen = "false";
    tooltip.setAttribute("aria-expanded", "false");
    tooltipContent.style.removeProperty("left");
    tooltipContent.style.removeProperty("right");
    tooltipContent.style.removeProperty("width");
    tooltipContent.style.removeProperty("max-width");

    if (activeTooltip === tooltip) {
      activeTooltip = null;
    }
  };

  const unwrapTooltipLinks = (tooltipContent) => {
    tooltipContent.querySelectorAll("a").forEach((link) => {
      link.replaceWith(document.createTextNode(link.textContent || ""));
    });
  };

  const positionTooltip = (tooltip) => {
    const tooltipContent = tooltip.querySelector(
      ".cryptonisation-altcoin-season__info-tooltip-content",
    );
    const widgetRoot = tooltip.closest(".cryptonisation-altcoin-season");

    if (!(tooltipContent instanceof HTMLElement)) {
      throw new Error("Tooltip content is missing.");
    }

    if (!(widgetRoot instanceof HTMLElement)) {
      throw new Error("Tooltip widget root is missing.");
    }

    const viewportWidth = window.innerWidth;
    const horizontalInset = 12;
    const widgetRect = widgetRoot.getBoundingClientRect();
    const allowedLeft = Math.max(
      horizontalInset,
      widgetRect.left + horizontalInset,
    );
    const allowedRight = Math.min(
      viewportWidth - horizontalInset,
      widgetRect.right - horizontalInset,
    );
    const availableWidth = Math.max(0, allowedRight - allowedLeft);

    tooltipContent.style.removeProperty("left");
    tooltipContent.style.removeProperty("right");
    tooltipContent.style.removeProperty("width");
    tooltipContent.style.removeProperty("max-width");
    tooltipContent.style.left = "0px";
    tooltipContent.style.right = "auto";
    tooltipContent.style.maxWidth = `${Math.min(360, availableWidth)}px`;

    const rect = tooltipContent.getBoundingClientRect();
    let offsetLeft = 0;

    if (rect.right > allowedRight) {
      offsetLeft -= rect.right - allowedRight;
    }

    if (rect.left + offsetLeft < allowedLeft) {
      offsetLeft += allowedLeft - (rect.left + offsetLeft);
    }

    tooltipContent.style.left = `${offsetLeft}px`;
  };

  const initializeTooltipInteractions = (widgetRoot) => {
    widgetRoot
      .querySelectorAll(".cryptonisation-altcoin-season__info-tooltip")
      .forEach((tooltip) => {
        const tooltipContent = tooltip.querySelector(
          ".cryptonisation-altcoin-season__info-tooltip-content",
        );

        if (!tooltipContent) {
          throw new Error("Tooltip content is missing.");
        }

        unwrapTooltipLinks(tooltipContent);
        tooltip.dataset.tooltipOpen = "false";
        tooltip.setAttribute("role", "button");
        tooltip.setAttribute("aria-expanded", "false");
        const tooltipId = `cryptonisation-altcoin-season-tooltip-${Math.random().toString(36).slice(2, 8)}`;
        tooltipContent.id = tooltipId;
        tooltip.setAttribute("aria-controls", tooltipId);

        tooltip.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (tooltip.dataset.tooltipOpen === "true") {
            closeTooltip(tooltip);
            return;
          }

          unwrapTooltipLinks(tooltipContent);
          openTooltip(tooltip);
        });

        tooltip.addEventListener("keydown", (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();

            if (tooltip.dataset.tooltipOpen === "true") {
              closeTooltip(tooltip);
              return;
            }

            unwrapTooltipLinks(tooltipContent);
            openTooltip(tooltip);
          }

          if (event.key === "Escape") {
            closeTooltip(tooltip);
            tooltip.blur();
          }
        });
      });
  };

  document.addEventListener("click", (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    if (event.target.closest(".cryptonisation-altcoin-season__info-tooltip")) {
      return;
    }

    closeTooltip(activeTooltip);
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") {
      return;
    }

    closeTooltip(activeTooltip);
  });

  const renderScale = (widgetRoot, payload, lang) => {
    const scaleRoot = widgetRoot.querySelector('[data-role="scale"]');

    if (!scaleRoot) {
      return;
    }

    const marker = scaleRoot.querySelector(
      ".cryptonisation-altcoin-season__scale-marker",
    );
    const markerLabel = scaleRoot.querySelector(
      ".cryptonisation-altcoin-season__scale-marker-label",
    );

    if (!marker || !markerLabel) {
      throw new Error("Scale markup is incomplete.");
    }

    marker.style.display = "";
    marker.style.left = `${payload.index_now}%`;
    markerLabel.textContent = formatNumber(lang, payload.index_now, 0);
  };

  const buildChartPath = (points, width, height, padding) => {
    if (points.length === 0) {
      return "";
    }

    return points
      .map((point, index) => {
        const x =
          padding.left +
          (index * (width - padding.left - padding.right)) /
            Math.max(points.length - 1, 1);
        const y =
          height -
          padding.bottom -
          (point.value / 100) * (height - padding.top - padding.bottom);
        return `${index === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(" ");
  };

  const buildXAxisLabelIndices = (pointCount, maxCount = 4) => {
    if (pointCount <= 1) {
      return [0];
    }

    if (maxCount <= 2) {
      return [...new Set([0, pointCount - 1])];
    }

    const lastIndex = pointCount - 1;
    const indices = [];

    for (let index = 0; index < maxCount; index += 1) {
      indices.push(Math.round((lastIndex * index) / (maxCount - 1)));
    }

    return [...new Set(indices)];
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const renderChart = (widgetRoot, payload, messages, lang, timeframe) => {
    const chartRoot = widgetRoot.querySelector('[data-role="chart"]');
    const plot = widgetRoot.querySelector('[data-role="chart-plot"]');
    const svg = widgetRoot.querySelector('[data-role="chart-svg"]');
    const empty = widgetRoot.querySelector('[data-role="chart-empty"]');
    const tooltip = widgetRoot.querySelector('[data-role="chart-tooltip"]');
    const axisLeft = widgetRoot.querySelector('[data-role="chart-axis-left"]');
    const axisBottom = widgetRoot.querySelector(
      '[data-role="chart-axis-bottom"]',
    );

    if (
      !chartRoot ||
      !plot ||
      !svg ||
      !empty ||
      !tooltip ||
      !axisLeft ||
      !axisBottom
    ) {
      return;
    }

    const history = Array.isArray(payload.history)
      ? payload.history.slice(-timeframe)
      : [];

    if (history.length === 0) {
      svg.innerHTML = "";
      axisLeft.innerHTML = "";
      axisBottom.innerHTML = "";
      empty.hidden = false;
      tooltip.hidden = true;
      return;
    }

    empty.hidden = true;

    const yAxisWidth = parseFloat(
      getComputedStyle(chartRoot).getPropertyValue('--cas-chart-y-axis-width')
    ) || 32;
    const plotRect = plot.getBoundingClientRect();
    const width = Math.max(1, plotRect.width - yAxisWidth);
    const height = Math.max(1, plotRect.height);
    const isMobile = plotRect.width < 640;
    const pointRadius = isMobile ? 2.2 : 3.2;
    const hitRadius = isMobile ? 9 : 12;
    const lineStrokeHalf = 1.5;

    const edgeInset = hitRadius + lineStrokeHalf;

    const padding = {
      top: pointRadius + 4,
      right: edgeInset,
      bottom: 12,
      left: edgeInset,
    };

    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.setAttribute("preserveAspectRatio", "none");

    const gridValues = [0, 25, 50, 75, 100];
    const path = buildChartPath(history, width, height, padding);

    const xForIndex = (index) =>
      padding.left +
      (index * (width - padding.left - padding.right)) /
        Math.max(history.length - 1, 1);
    const yForValue = (value) =>
      height -
      padding.bottom -
      (value / 100) * (height - padding.top - padding.bottom);

    const lines = gridValues
      .map((value) => {
        const y = yForValue(value);
        return `<line class="cryptonisation-altcoin-season__chart-grid-line" x1="${padding.left}" y1="${y}" x2="${width - padding.right}" y2="${y}"></line>`;
      })
      .join("");

    const yLabels = gridValues
      .map((value) => {
        const y = yForValue(value);
        const topPercent = (y / height) * 100;
        return `<span class="cryptonisation-altcoin-season__chart-axis-label" style="right:5px; top:${topPercent.toFixed(2)}%; transform:translateY(-50%);">${formatNumber(lang, value, 0)}</span>`;
      })
      .join("");

    const zones = [
      { className: "bitcoin", from: 0, to: 25 },
      { className: "neutral", from: 25, to: 75 },
      { className: "altcoin", from: 75, to: 100 },
    ]
      .map((zone) => {
        const topY = yForValue(zone.to);
        const bottomY = yForValue(zone.from);
        const zoneHeight = Math.max(0, bottomY - topY);
        return `<rect class="cryptonisation-altcoin-season__chart-zone cryptonisation-altcoin-season__chart-zone--${zone.className}" x="${padding.left}" y="${topY}" width="${width - padding.left - padding.right}" height="${zoneHeight}"></rect>`;
      })
      .join("");

    const labels = buildXAxisLabelIndices(history.length, isMobile ? 3 : 5)
      .map((index) => {
        const point = history[index];
        const x = xForIndex(index);
        const axisPercent = (x / width) * 100;
        let labelStyle = `left:${axisPercent.toFixed(2)}%; transform:translateX(-50%);`;
        if (index === 0) {
          labelStyle = "left:0; transform:none;";
        } else if (index === history.length - 1) {
          labelStyle = "right:0; left:auto; transform:none; text-align:right;";
        }
        return `<span class="cryptonisation-altcoin-season__chart-axis-label" style="${labelStyle}">${escapeHtml(formatDate(lang, point.date))}</span>`;
      })
      .join("");

    const points = history
      .map((point, index) => {
        const x = xForIndex(index);
        const y = yForValue(point.value);
        return `
          <circle vector-effect="non-scaling-stroke" class="cryptonisation-altcoin-season__chart-point" cx="${x}" cy="${y}" r="${pointRadius}"></circle>
          <circle
            class="cryptonisation-altcoin-season__chart-point-hit"
            data-date="${escapeHtml(formatDate(lang, point.date))}"
            data-value="${escapeHtml(formatNumber(lang, point.value, 0))}"
            data-svg-x="${x.toFixed(2)}"
            data-svg-y="${y.toFixed(2)}"
            cx="${x}"
            cy="${y}"
            r="${hitRadius}"
          ></circle>
        `;
      })
      .join("");

    svg.innerHTML = `
    ${zones}
    ${lines}
    <path vector-effect="non-scaling-stroke" class="cryptonisation-altcoin-season__chart-line" d="${path}"></path>
    ${points}
  `;

    axisLeft.innerHTML = yLabels;
    axisBottom.innerHTML = labels;

    const positionTooltip = (anchorX, anchorY) => {
      const bounds = plot.getBoundingClientRect();
      const tooltipWidth = tooltip.offsetWidth || 0;
      const tooltipHeight = tooltip.offsetHeight || 0;
      const arrowHeight = 7;
      const gap = 8;
      const left = clamp(
        anchorX - tooltipWidth / 2,
        8,
        Math.max(8, bounds.width - tooltipWidth - 8),
      );
      const top = Math.max(8, anchorY - tooltipHeight - arrowHeight - gap);
      const arrowLeft = clamp(
        anchorX - left,
        16,
        Math.max(16, tooltipWidth - 16),
      );

      tooltip.style.left = `${left.toFixed(2)}px`;
      tooltip.style.top = `${top.toFixed(2)}px`;
      tooltip.style.setProperty("--arrow-offset", `${arrowLeft}px`);
    };

    const showTooltip = (point) => {
      const date = point.getAttribute("data-date");
      const value = point.getAttribute("data-value");
      const svgX = Number(point.getAttribute("data-svg-x"));
      const svgY = Number(point.getAttribute("data-svg-y"));

      if (!date || !value || Number.isNaN(svgX) || Number.isNaN(svgY)) {
        throw new Error("Chart tooltip payload is incomplete.");
      }

      tooltip.innerHTML = `
        <div class="cnbd-tooltip-date">${escapeHtml(date)}</div>
        <div class="cnbd-tooltip-row"><span>${escapeHtml(messages.chart_tooltip_label)}: ${escapeHtml(value)}</span></div>
      `;
      tooltip.hidden = false;

      const chartBounds = plot.getBoundingClientRect();
      const svgBounds = svg.getBoundingClientRect();
      const anchorX =
        (svgX / width) * svgBounds.width + (svgBounds.left - chartBounds.left);
      const anchorY =
        (svgY / height) * svgBounds.height + (svgBounds.top - chartBounds.top);

      positionTooltip(anchorX, anchorY);
    };

    const hideTooltip = () => {
      tooltip.hidden = true;
    };

    svg
      .querySelectorAll(".cryptonisation-altcoin-season__chart-point-hit")
      .forEach((point) => {
        point.addEventListener("mouseenter", () => {
          showTooltip(point);
        });

        point.addEventListener("mousemove", () => {
          showTooltip(point);
        });

        point.addEventListener("click", () => {
          showTooltip(point);
        });

        point.addEventListener(
          "touchstart",
          () => {
            showTooltip(point);
          },
          { passive: true },
        );

        point.addEventListener("mouseleave", hideTooltip);
      });

    chartRoot.onmouseleave = hideTooltip;
  };

  const bindChartResize = (widgetRoot) => {
    const chartRoot = widgetRoot.querySelector('[data-role="chart"]');

    if (!chartRoot) {
      return;
    }

    const redraw = () => {
      if (!hasRenderablePayload(widgetRoot.altcoinSeasonPayload)) {
        return;
      }

      renderChart(
        widgetRoot,
        widgetRoot.altcoinSeasonPayload,
        widgetRoot.altcoinSeasonMessages,
        widgetRoot.altcoinSeasonLang,
        widgetRoot.altcoinSeasonTimeframe,
      );
    };

    if (typeof window.ResizeObserver === "function") {
      const resizeObserver = new ResizeObserver(redraw);
      resizeObserver.observe(chartRoot);
      widgetRoot.altcoinSeasonChartResizeObserver = resizeObserver;
      return;
    }

    window.addEventListener("resize", redraw);
    widgetRoot.altcoinSeasonChartResizeHandler = redraw;
  };

  const splitCompositionCoins = (coins, isLeader) => {
    const filteredCoins = coins.filter((coin) => {
      return (
        coin &&
        typeof coin.coin === "string" &&
        Number.isInteger(coin.rank) &&
        typeof coin.outperformed_btc === "boolean" &&
        typeof coin.return_90d === "number" &&
        coin.outperformed_btc === isLeader
      );
    });

    return filteredCoins.sort((left, right) => {
      if (left.return_90d === right.return_90d) {
        return left.rank - right.rank;
      }

      return right.return_90d - left.return_90d;
    });
  };

  const resolveVisibleCompositionCoins = (coins, isExpanded) => {
    if (isExpanded) {
      return coins;
    }

    return coins.slice(0, 15);
  };

  const formatSignedPercent = (lang, value) =>
    `${value > 0 ? "+" : ""}${formatNumber(lang, value, 2)}%`;

  const formatRelativePercent = (lang, value) =>
    `(${formatSignedPercent(lang, value)})`;

  const returnModifier = (value) => (value >= 0 ? "positive" : "negative");

  const renderCompositionList = (widgetRoot, selector, coins, lang) => {
    const root = widgetRoot.querySelector(selector);

    if (!root) {
      return;
    }

    root.innerHTML = coins
      .map((coin) => {
        if (
          typeof coin.coin !== "string" ||
          !Number.isInteger(coin.rank) ||
          typeof coin.return_90d !== "number" ||
          typeof coin.btc_return_90d !== "number"
        ) {
          throw new Error("Composition list coin is missing required fields.");
        }

        const relativeReturn = coin.return_90d - coin.btc_return_90d;

        return `
          <div class="cryptonisation-altcoin-season__composition-item">
            <div class="cryptonisation-altcoin-season__composition-cell cryptonisation-altcoin-season__composition-cell--coin">
              ${escapeHtml(coin.coin)}
            </div>
            <div class="cryptonisation-altcoin-season__composition-cell">
              ${escapeHtml(formatNumber(lang, coin.rank, 0))}
            </div>
            <div class="cryptonisation-altcoin-season__composition-cell cryptonisation-altcoin-season__composition-cell--return-group">
              <span class="cryptonisation-altcoin-season__composition-return-value cryptonisation-altcoin-season__composition-return-value--primary">
                ${escapeHtml(formatSignedPercent(lang, coin.return_90d))}
              </span>
              <span class="cryptonisation-altcoin-season__composition-return-value cryptonisation-altcoin-season__composition-return-value--secondary cryptonisation-altcoin-season__composition-return-value--${escapeHtml(returnModifier(relativeReturn))}">
                ${escapeHtml(formatRelativePercent(lang, relativeReturn))}
              </span>
            </div>
          </div>
        `;
      })
      .join("");
  };

  const setCompositionCount = (widgetRoot, role, count, lang) => {
    const root = widgetRoot.querySelector(`[data-role="${role}"]`);

    if (!root) {
      return;
    }

    root.textContent = formatNumber(lang, count, 0);
  };

  const updateCompositionToggle = (
    widgetRoot,
    role,
    isVisible,
    isExpanded,
    messages,
  ) => {
    const root = widgetRoot.querySelector(`[data-role="${role}"]`);

    if (!root) {
      return;
    }

    root.hidden = !isVisible;
    root.textContent = isExpanded
      ? messages.show_less_label
      : messages.show_more_label;
  };

  const renderStreak = (widgetRoot, payload, messages, lang) => {
    const root = widgetRoot.querySelector('[data-role="status-streak"]');

    if (!root || !payload.status_streak) {
      return;
    }

    root.textContent = formatDurationDays(
      lang,
      payload.status_streak.days,
      messages,
    );
  };

  const renderUpdatedAt = (widgetRoot, payload, messages, lang) => {
    const root = widgetRoot.querySelector('[data-role="updated-at"]');

    if (!root) {
      return;
    }

    const date = new Date(payload.updated_at);
    root.textContent = `${messages.updated_at_label}: ${formatDateTime(lang, payload.updated_at)} (${formatBrowserTimezoneOffset(date)})`;
  };

  const setLoadState = (widgetRoot, title, message, className) => {
    const root = widgetRoot.querySelector('[data-role="load-state"]');

    if (!root) {
      return;
    }

    root.className = className;
    root.innerHTML = `<strong>${escapeHtml(title)}</strong><p>${escapeHtml(message)}</p>`;
    root.hidden = false;
  };

  const renderPrimaryStats = (widgetRoot, payload, messages, lang) => {
    const statusLabelMap = {
      bitcoin: messages.status_bitcoin,
      neutral: messages.status_neutral,
      altcoin: messages.status_altcoin,
    };
    const scoreClassForValue = (value) => {
      if (value <= 25) {
        return "bitcoin";
      }

      if (value >= 75) {
        return "altcoin";
      }

      return "neutral";
    };
    const scoreLabelForValue = (value) => {
      const statusKey = scoreClassForValue(value);

      return statusLabelMap[statusKey] || "";
    };
    const resolveHistoryDateByValue = (value) => {
      const history = Array.isArray(payload.history) ? payload.history : [];
      const matchedPoint = history.find(
        (point) => point && point.value === value,
      );

      if (!matchedPoint || typeof matchedPoint.date !== "string") {
        return "";
      }

      return formatDate(lang, matchedPoint.date);
    };

    const setText = (role, value) => {
      const node = widgetRoot.querySelector(`[data-role="${role}"]`);

      if (node) {
        node.textContent = value;
      }
    };

    const setDistributionScale = (outperformedValue, underperformedValue) => {
      const outperformedSegment = widgetRoot.querySelector(
        '[data-role="outperformed-segment"]',
      );
      const underperformedSegment = widgetRoot.querySelector(
        '[data-role="underperformed-segment"]',
      );
      const outperformedCount = widgetRoot.querySelector(
        '[data-role="outperformed-count"]',
      );
      const underperformedCount = widgetRoot.querySelector(
        '[data-role="underperformed-count"]',
      );
      const normalizedOutperformedValue = Number(outperformedValue);
      const normalizedUnderperformedValue = Number(underperformedValue);

      if (
        !outperformedSegment ||
        !underperformedSegment ||
        !outperformedCount ||
        !underperformedCount ||
        Number.isNaN(normalizedOutperformedValue) ||
        Number.isNaN(normalizedUnderperformedValue)
      ) {
        return;
      }

      const clampedOutperformedValue = Math.max(0, normalizedOutperformedValue);
      const clampedUnderperformedValue = Math.max(
        0,
        normalizedUnderperformedValue,
      );

      outperformedSegment.style.flexGrow = String(clampedOutperformedValue);
      underperformedSegment.style.flexGrow = String(clampedUnderperformedValue);
      outperformedCount.textContent = formatNumber(
        lang,
        clampedOutperformedValue,
        0,
      );
      underperformedCount.textContent = formatNumber(
        lang,
        clampedUnderperformedValue,
        0,
      );
    };

    const setScoreChip = (role, value) => {
      const node = widgetRoot.querySelector(`[data-role="${role}"]`);

      if (!node) {
        return;
      }

      node.classList.remove(
        "cryptonisation-altcoin-season__score-chip--bitcoin",
        "cryptonisation-altcoin-season__score-chip--neutral",
        "cryptonisation-altcoin-season__score-chip--altcoin",
      );
      node.classList.add(
        `cryptonisation-altcoin-season__score-chip--${scoreClassForValue(value)}`,
      );
      node.textContent = `${scoreLabelForValue(value)} - ${formatNumber(lang, value, 0)}`;
    };

    setText("status-value", `${formatNumber(lang, payload.index_now, 0)}/100`);
    setScoreChip("yesterday-chip", payload.yesterday);
    setScoreChip("week-ago-chip", payload.week_ago);
    setScoreChip("month-ago-chip", payload.month_ago);
    setScoreChip("year-high-chip", payload.year_high);
    setScoreChip("year-low-chip", payload.year_low);
    setText("year-high-date", resolveHistoryDateByValue(payload.year_high));
    setText("year-low-date", resolveHistoryDateByValue(payload.year_low));
    setDistributionScale(
      payload.distribution.outperformed_btc,
      payload.distribution.underperformed_btc,
    );
    setText(
      "excluded-list",
      Array.isArray(payload.excluded) ? payload.excluded.join(", ") : "",
    );
    renderStreak(widgetRoot, payload, messages, lang);

    const statusCard = widgetRoot.querySelector(
      ".cryptonisation-altcoin-season__status-card",
    );
    const statusBadge = widgetRoot.querySelector(
      ".cryptonisation-altcoin-season__status-badge",
    );
    const statusClass =
      STATUS_CLASS_MAP[payload.status] || STATUS_CLASS_MAP.neutral;

    if (statusCard) {
      statusCard.classList.remove(
        "cryptonisation-altcoin-season__status-card--bitcoin",
        "cryptonisation-altcoin-season__status-card--neutral",
        "cryptonisation-altcoin-season__status-card--altcoin",
      );
      statusCard.classList.add(
        `cryptonisation-altcoin-season__status-card--${statusClass}`,
      );
    }

    if (statusBadge) {
      statusBadge.classList.remove(
        "cryptonisation-altcoin-season__status-badge--bitcoin",
        "cryptonisation-altcoin-season__status-badge--neutral",
        "cryptonisation-altcoin-season__status-badge--altcoin",
      );
      statusBadge.classList.add(
        `cryptonisation-altcoin-season__status-badge--${statusClass}`,
      );
      statusBadge.textContent =
        statusLabelMap[payload.status] || payload.status;
    }

    const loadState = widgetRoot.querySelector('[data-role="load-state"]');

    if (loadState) {
      loadState.hidden = true;
    }
  };

  const renderWidget = (widgetRoot, payload, messages, timeframe) => {
    const lang = widgetRoot.getAttribute("data-lang");

    if (!lang) {
      throw new Error("Widget language is not defined.");
    }

    renderPrimaryStats(widgetRoot, payload, messages, lang);
    renderScale(widgetRoot, payload, lang);
    renderChart(widgetRoot, payload, messages, lang, timeframe);
    const coins = Array.isArray(payload.coins) ? payload.coins : [];
    const leaderCoins = splitCompositionCoins(coins, true);
    const laggardCoins = splitCompositionCoins(coins, false);
    const leaderCoinsVisible = resolveVisibleCompositionCoins(
      leaderCoins,
      widgetRoot.altcoinSeasonCompositionLeadersExpanded === true,
    );
    const laggardCoinsVisible = resolveVisibleCompositionCoins(
      laggardCoins,
      widgetRoot.altcoinSeasonCompositionLaggardsExpanded === true,
    );
    renderCompositionList(
      widgetRoot,
      '[data-role="composition-leaders"]',
      leaderCoinsVisible,
      lang,
    );
    renderCompositionList(
      widgetRoot,
      '[data-role="composition-laggards"]',
      laggardCoinsVisible,
      lang,
    );
    setCompositionCount(
      widgetRoot,
      "composition-leaders-count",
      leaderCoins.length,
      lang,
    );
    setCompositionCount(
      widgetRoot,
      "composition-laggards-count",
      laggardCoins.length,
      lang,
    );
    updateCompositionToggle(
      widgetRoot,
      "composition-toggle-desktop",
      leaderCoins.length > 15 || laggardCoins.length > 15,
      widgetRoot.altcoinSeasonCompositionLeadersExpanded === true &&
        widgetRoot.altcoinSeasonCompositionLaggardsExpanded === true,
      messages,
    );
    updateCompositionToggle(
      widgetRoot,
      "composition-toggle-leaders-mobile",
      leaderCoins.length > 15,
      widgetRoot.altcoinSeasonCompositionLeadersExpanded === true,
      messages,
    );
    updateCompositionToggle(
      widgetRoot,
      "composition-toggle-laggards-mobile",
      laggardCoins.length > 15,
      widgetRoot.altcoinSeasonCompositionLaggardsExpanded === true,
      messages,
    );
    renderUpdatedAt(widgetRoot, payload, messages, lang);
  };

  const hasRenderablePayload = (payload) =>
    payload &&
    typeof payload === "object" &&
    Array.isArray(payload.coins) &&
    Array.isArray(payload.history) &&
    typeof payload.status === "string";

  widgetRoots.forEach((widgetRoot) => {
    const initialPayload = readJsonScript(
      widgetRoot,
      ".cryptonisation-altcoin-season__payload-json",
    );
    const messages = readJsonScript(
      widgetRoot,
      ".cryptonisation-altcoin-season__messages-json",
    );
    const widgetLang = widgetRoot.getAttribute("data-lang");

    if (!widgetLang) {
      throw new Error("Widget language is not defined.");
    }

    widgetRoot.altcoinSeasonMessages = messages;
    widgetRoot.altcoinSeasonPayload = initialPayload;
    widgetRoot.altcoinSeasonTimeframe = 90;
    widgetRoot.altcoinSeasonLang = widgetLang;
    widgetRoot.altcoinSeasonCompositionLeadersExpanded = false;
    widgetRoot.altcoinSeasonCompositionLaggardsExpanded = false;
    initializeTooltipInteractions(widgetRoot);
    bindChartResize(widgetRoot);

    if (hasRenderablePayload(initialPayload)) {
      renderWidget(
        widgetRoot,
        initialPayload,
        messages,
        widgetRoot.altcoinSeasonTimeframe,
      );
    } else {
      setLoadState(
        widgetRoot,
        messages.empty_state_title,
        messages.empty_state_message,
        "cryptonisation-altcoin-season__notice",
      );
    }

    widgetRoot.querySelectorAll("[data-timeframe]").forEach((button) => {
      button.addEventListener("click", () => {
        const timeframe = Number(button.getAttribute("data-timeframe"));

        if (!TIMEFRAMES.includes(timeframe)) {
          throw new Error(`Unsupported timeframe: ${timeframe}`);
        }

        widgetRoot.altcoinSeasonTimeframe = timeframe;
        widgetRoot.querySelectorAll("[data-timeframe]").forEach((candidate) => {
          candidate.classList.toggle(
            "cryptonisation-altcoin-season__timeframe-button--active",
            candidate === button,
          );
        });
        renderChart(
          widgetRoot,
          widgetRoot.altcoinSeasonPayload,
          widgetRoot.altcoinSeasonMessages,
          widgetRoot.altcoinSeasonLang,
          timeframe,
        );
      });
    });

    const compositionToggleDesktop = widgetRoot.querySelector(
      '[data-role="composition-toggle-desktop"]',
    );

    if (compositionToggleDesktop) {
      compositionToggleDesktop.addEventListener("click", () => {
        const shouldExpand = !(
          widgetRoot.altcoinSeasonCompositionLeadersExpanded === true &&
          widgetRoot.altcoinSeasonCompositionLaggardsExpanded === true
        );

        widgetRoot.altcoinSeasonCompositionLeadersExpanded = shouldExpand;
        widgetRoot.altcoinSeasonCompositionLaggardsExpanded = shouldExpand;
        renderWidget(
          widgetRoot,
          widgetRoot.altcoinSeasonPayload,
          widgetRoot.altcoinSeasonMessages,
          widgetRoot.altcoinSeasonTimeframe,
        );
      });
    }

    const compositionToggleLeadersMobile = widgetRoot.querySelector(
      '[data-role="composition-toggle-leaders-mobile"]',
    );

    if (compositionToggleLeadersMobile) {
      compositionToggleLeadersMobile.addEventListener("click", () => {
        widgetRoot.altcoinSeasonCompositionLeadersExpanded =
          widgetRoot.altcoinSeasonCompositionLeadersExpanded !== true;
        renderWidget(
          widgetRoot,
          widgetRoot.altcoinSeasonPayload,
          widgetRoot.altcoinSeasonMessages,
          widgetRoot.altcoinSeasonTimeframe,
        );
      });
    }

    const compositionToggleLaggardsMobile = widgetRoot.querySelector(
      '[data-role="composition-toggle-laggards-mobile"]',
    );

    if (compositionToggleLaggardsMobile) {
      compositionToggleLaggardsMobile.addEventListener("click", () => {
        widgetRoot.altcoinSeasonCompositionLaggardsExpanded =
          widgetRoot.altcoinSeasonCompositionLaggardsExpanded !== true;
        renderWidget(
          widgetRoot,
          widgetRoot.altcoinSeasonPayload,
          widgetRoot.altcoinSeasonMessages,
          widgetRoot.altcoinSeasonTimeframe,
        );
      });
    }
  });
})();
